import { Mic } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import { Button } from '../button.tsx';
import { TranscriptBubble } from './transcript-bubble.tsx';
import { WaveformStrip } from './waveform-strip.tsx';

export interface LiveTurn {
  id: string;
  speaker: 'guest' | 'hostesa';
  speakerLabel: string;
  lang?: string;
  text: string;
}

export interface LiveCallPanelProps {
  line?: number;
  caller?: { name: string; meta?: string };
  turns: LiveTurn[];
  liveLabel?: string;
  lineLabel?: string;
  whisperLabel?: string;
  takeOverLabel?: string;
  emptyLabel?: string;
  onWhisper?: () => void;
  onTakeOver?: () => void;
  className?: string;
  'data-testid'?: string;
}

/**
 * LiveCallPanel — the right-rail live conversation view. Provider-independent:
 * it renders canonical turns + handoff actions, never provider SDK detail. When
 * there is no active call it shows a calm, reassuring empty state.
 */
export function LiveCallPanel({
  line,
  caller,
  turns,
  liveLabel = 'Live',
  lineLabel = 'Line',
  whisperLabel = 'Whisper',
  takeOverLabel = 'Take over',
  emptyLabel = 'No live calls right now',
  onWhisper,
  onTakeOver,
  className,
  'data-testid': testId,
}: LiveCallPanelProps) {
  const hasCall = Boolean(caller) || turns.length > 0;
  return (
    <section
      data-testid={testId}
      aria-label={liveLabel}
      className={cn(
        'flex flex-col gap-4 rounded-3xl border border-[var(--color-border)] bg-[var(--color-alabaster)] p-5',
        'shadow-[var(--shadow-panel)]',
        className,
      )}
    >
      {!hasCall ? (
        <div
          data-testid="live-call-empty"
          className="flex flex-col items-center gap-1.5 py-12 text-center"
        >
          <p className="font-[var(--font-display)] text-base font-medium text-[var(--color-charcoal)]">
            {emptyLabel}
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 font-[var(--font-mono)] text-[11px] uppercase tracking-widest">
            <span aria-hidden="true" className="h-2 w-2 rounded-full bg-[var(--color-alert)]" />
            <span className="text-[var(--color-alert)]">{liveLabel}</span>
            {typeof line === 'number' && (
              <span className="text-[var(--color-stone-mid)]">
                · {lineLabel} {line}
              </span>
            )}
          </div>
          {caller && (
            <div className="flex flex-col gap-0.5">
              <span className="font-[var(--font-display)] text-xl font-medium tracking-tight text-[var(--color-charcoal)]">
                {caller.name}
              </span>
              {caller.meta && (
                <span className="font-[var(--font-mono)] text-xs text-[var(--color-stone-mid)]">
                  {caller.meta}
                </span>
              )}
            </div>
          )}
          <WaveformStrip />
          <div className="flex max-h-72 flex-col gap-3 overflow-y-auto pr-1">
            {turns.map((turn) => (
              <TranscriptBubble
                key={turn.id}
                speaker={turn.speaker}
                speakerLabel={turn.speakerLabel}
                lang={turn.lang}
                text={turn.text}
                data-testid={`live-call-turn-${turn.id}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Button
              data-testid="live-call-whisper-btn"
              aria-label={whisperLabel}
              variant="primary"
              size="sm"
              onClick={onWhisper}
            >
              <Mic aria-hidden="true" className="size-3.5" />
              {whisperLabel}
            </Button>
            <Button
              data-testid="live-call-takeover-btn"
              aria-label={takeOverLabel}
              variant="secondary"
              size="sm"
              onClick={onTakeOver}
            >
              {takeOverLabel}
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
